import React, { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import styles from './index.module.less';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import {
  cleanExpiredStories,
  saveStoryToLocalStorage,
} from '@/components/LandingPage/SearchResultContent/StoriesListContent';
import classNames from 'classnames';
import {
  InstagramAnonymouslyHighlightsModel,
  InstagramAnonymouslyStoriesModel,
  InstagramQueryPostItemModel,
} from '@/types/anonymously.interface';
import SVG from 'react-inlinesvg';
import { Button, message, Tooltip } from 'antd';
import LockedWrapper from '@/components/Report/LockedWrapper';
import useCheckIsMobile from '@/lib/hooks/useCheckIsMobile';
import useRandomAd from '@/lib/hooks/anonymous-instagram-story-viewer/useRandomAd';
import useMediaQuery from '@/lib/hooks/useMediaQuery';
import Link from 'next/link';
import { DownloadIcon, MoveRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getInsStoryDownload } from '@/lib/service/dashboard';
import dayjs from 'dayjs';
import { useUserProvider } from '@/context/UserProvider';
import { LimitAlert } from '@/components/LandingPage/FeeLimitAlert';
import { fetchInstagramAnonymouslyHighlightsDetailDataClient } from '@/lib/service/anonymously';

type StoryOrAd = InstagramAnonymouslyStoriesModel | { type: 'ad-card' };

const isStory = (item: StoryOrAd): item is InstagramAnonymouslyStoriesModel => {
  return !('type' in item);
};

// CLS 优化：关键组件使用同步导入，避免异步加载导致的布局偏移
import LocalImage from '@/components/Common/LocalImage';
import StoriesLinkContent from '@/components/LandingPage/SearchResultContent/StoriesLinkContent';
import PostResultContent from '../PostResultContent';
const VideoPlayContent = dynamic(() => import('@/components/VideoPlayContent'), {
  ssr: false,
});

const InstagramStoryContent = ({
  locked = false,
  showDownload = false,
  loadMoreNotCheckLogin,
  hideTitle = false,
  analyzeAll = false,
  tabFunc = 'story',
  media_name,
  stories,
  highlights_list,
  post_page_token,
  post_list,
  post_num,
  onDownloadClick,
  gotoPlanClick,
  onUnlock,
  isDemoData,
}: {
  locked?: boolean;
  showDownload?: boolean;
  loadMoreNotCheckLogin?: boolean;
  hideTitle?: boolean;
  analyzeAll?: boolean;
  tabFunc?: 'story' | 'highlights' | 'post';
  media_name?: string;
  stories?: InstagramAnonymouslyStoriesModel[];
  highlights_list?: InstagramAnonymouslyHighlightsModel[];
  post_list?: InstagramQueryPostItemModel[];
  post_num?: number;
  post_page_token?: string;
  onDownloadClick?: () => void;
  gotoPlanClick?: () => void;
  onUnlock?: () => void;
  isDemoData?: boolean;
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { locale } = router;
  const { isMobile } = useCheckIsMobile();
  const [index, setIndex] = useState(1);
  const [highlightsIndex, setHighlightsIndex] = useState(1);
  const [tabIndexSelect, setTabIndexSelect] = useState<'story' | 'highlights' | 'post'>(tabFunc);
  const [storyFilter, setStoryFilter] = useState<'ALL' | 'MENTIONS' | 'LINKS'>('ALL');

  const currentAd = useRandomAd();
  const screenLessThan900 = useMediaQuery('(max-width: 900px)');
  const screenLessThan680 = useMediaQuery('(max-width: 680px)');
  const { isLogin, onOpenLoginModal, onChangedLoginView } = useUserProvider();
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [showLimit, setShowLimit] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentStoryData, setCurrentStoryData] = useState<InstagramAnonymouslyStoriesModel | null>(null);
  const [showHighlightModal, setShowHighlightModal] = useState(false);
  const [currentHighlightData, setCurrentHighlightData] = useState<any>(null);
  const [highlightLoading, setHighlightLoading] = useState(false);
  const [mounted, setMounted] = useState(false);

  console.log('highlightsIndex', highlightsIndex);
  console.log('highlightsIndex', tabIndexSelect);

  useEffect(() => {
    setMounted(true);
  }, []);

  const filteredStories = useMemo(() => {
    if (!stories || stories.length === 0) return [];

    switch (storyFilter) {
      case 'MENTIONS':
        return stories.filter((item) => item?.mention_names && item.mention_names.length > 0);
      case 'LINKS':
        return stories.filter((item) => item?.link_urls && item.link_urls.length > 0);
      case 'ALL':
      default:
        return stories;
    }
  }, [stories, storyFilter]);

  const showStoriesMore = useMemo(() => {
    if (locked) {
      return false;
    }
    return index * 8 < filteredStories?.length;
  }, [index, filteredStories, locked]);

  const showStoriesList = useMemo<StoryOrAd[]>(() => {
    let list: StoryOrAd[];
    if (!filteredStories.length) return [];
    if (index * 8 < filteredStories?.length) {
      list = filteredStories?.slice(0, index * 8);
    } else {
      list = [...filteredStories];
    }
    list = locked ? list.slice(0, 3) : list;
    // Only show ad card after client-side mount to avoid hydration errors
    if (!mounted || !currentAd || isDemoData) return list;
    const rowCount = screenLessThan680 ? 2 : screenLessThan900 ? 3 : 4;
    if (list.length < rowCount) {
      list.push({ type: 'ad-card' });
    } else {
      list.splice(rowCount, 0, { type: 'ad-card' });
    }
    return list;
  }, [index, filteredStories, locked, currentAd, screenLessThan680, screenLessThan900, isDemoData, mounted]);

  const showHighlightsMore = useMemo(() => {
    if (locked) {
      return false;
    }
    return highlightsIndex * 16 < highlights_list?.length;
  }, [highlightsIndex, highlights_list, locked]);

  const showHighlightsList = useMemo(() => {
    if (highlightsIndex * 16 < highlights_list?.length) {
      return highlights_list?.slice(0, highlightsIndex * 16);
    } else {
      return highlights_list;
    }
  }, [highlightsIndex, highlights_list]);

  const tabs = useMemo(() => {
    return [
      {
        label: t('Stories'),
        key: 'story',
        value: stories?.length ?? 0,
        icon: '/images/landingpage/stories_icon.svg',
        active: tabIndexSelect === 'story',
      },
      {
        label: t('Posts'),
        key: 'post',
        // 优先使用 post_num，如果 post_num 不存在，则使用 post_list 的长度
        value: (post_num || post_list?.length) ?? 0,
        icon: '/images/landingpage/camera_icon.svg',
        active: tabIndexSelect === 'post',
      },
      {
        label: t('Highlights'),
        key: 'highlights',
        value: highlights_list?.length ?? 0,
        icon: '/images/landingpage/high_lights_icon.svg',
        active: tabIndexSelect === 'highlights',
        tip: t(
          'The Instagram Highlights display shows the total number of posts, unaffected by time filters. As new highlights are added, this count will update accordingly.'
        ),
      },
    ];
  }, [t, stories, highlights_list, post_list, post_num, tabIndexSelect]);

  useEffect(() => {
    setIndex(1);
    setHighlightsIndex(1);
    setStoryFilter('ALL');
  }, [stories, highlights_list, post_list]);

  useEffect(() => {
    // 切换筛选条件后重置索引
    setIndex(1);
  }, [storyFilter]);

  const getTitleContent = (tip: string) => {
    return (
      <div
        style={{
          color: '#fff',
          padding: '10px',
        }}
      >
        {tip}
      </div>
    );
  };

  const storyFilterOptions = [
    { key: 'ALL', label: t('All') },
    { key: 'MENTIONS', label: t('Stories with Mentions (@)') },
    { key: 'LINKS', label: t('Stories with Links') },
  ];

  const handelStoriesListItemClick = function (item: InstagramAnonymouslyStoriesModel) {
    cleanExpiredStories();
    saveStoryToLocalStorage(item);
    // 设置当前故事数据并显示全屏蒙层
    setCurrentStoryData(item);
    setShowVideoModal(true);
  };

  const onItemDownloadClick = (currentItem: InstagramAnonymouslyStoriesModel) => {
    if (!isLogin) {
      onChangedLoginView(false);
      onOpenLoginModal(true);
      return;
    }
    if (downloadLoading) {
      return;
    }
    setDownloadLoading(true);
    getInsStoryDownload(`${media_name}`, 'single', currentItem?.publish_time?.split(' ')[0])
      .then((res) => {
        if (res?.code === 0) {
          // window.location.href = res.data;
          handleDownload(currentItem);
          return;
        }
        if (res?.code === 22010 || res?.code === 22011) {
          setShowLimit(true);
          return;
        } else {
          message.error(res?.message);
        }
      })
      .finally(() => {
        setDownloadLoading(false);
      });
  };

  const handleDownload = async (currentItem: InstagramAnonymouslyStoriesModel) => {
    if (!isLogin) {
      onChangedLoginView(false);
      onOpenLoginModal(true);
      return;
    }
    if (
      (`${currentItem?.media_type}` === '2' && currentItem?.video_url?.length > 0) ||
      (`${currentItem?.media_type}` === '1' && currentItem?.cover_image?.length > 0)
    ) {
      let imgUrlStr;
      if (`${currentItem?.media_type}` === '2' && currentItem?.video_url?.length > 0) {
        imgUrlStr = currentItem?.video_url;
      } else if (`${currentItem?.media_type}` === '1' && currentItem?.cover_image?.length > 0) {
        imgUrlStr = currentItem?.cover_image;
      }
      if (!imgUrlStr) return;

      try {
        const response = await fetch(`/n-api/download-image?url=${encodeURIComponent(imgUrlStr)}`);
        if (!response.ok) {
          throw new Error('Failed to download file');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        if (`${currentItem?.media_type}` === '2') {
          a.download = `Dolphin_Radar_${dayjs().format('YYYYMMDDHHmmss')}.mp4`;
        } else {
          a.download = `Dolphin_Radar_${dayjs().format('YYYYMMDDHHmmss')}.png`;
        }
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading image:', error);
      }
    }
  };

  return (
    <div className={styles.content}>
      <div className={styles.dataContainer}>
        <div className={styles.header}>
          <div className={styles.title}>
            <div className={styles.tabContainer}>
              {tabs?.map((item) => {
                return (
                  <div
                    key={item.key}
                    className={classNames([styles.tabItem, item.active && styles.tabItemActive])}
                    onClick={() => setTabIndexSelect(item.key as 'story' | 'highlights' | 'post')}
                  >
                    <SVG
                      src={item.icon}
                      width={'15px'}
                      height={'15px'}
                      title="svg"
                      style={{ color: item.active ? '#725CFF' : '#000' }}
                    />
                    <span>{item.label}</span>
                    <span className={styles.count}>{locked ? '' : item.value}</span>
                    {item?.tip && (
                      <Tooltip title={getTitleContent(item?.tip)}>
                        <Image
                          src={'/images/plan/question_icon_1.webp'}
                          alt="plan-question-icon.webp"
                          width={14}
                          height={14}
                          className={styles.questionIcon}
                        />
                      </Tooltip>
                    )}
                  </div>
                );
              })}
            </div>

            {showDownload && (
              <button className={styles.batchDownload} onClick={onDownloadClick}>
                <Image src="/images/landingpage/download_icon_2.webp" alt="" width={16} height={16} />
                {!isMobile && <span>{t('Batch Download')}</span>}
              </button>
            )}
          </div>
        </div>

        <div className={styles.storiesSection} style={{ display: tabIndexSelect === 'story' ? 'block' : 'none' }}>
          <div className={styles.sectionTitle}>
            {!locked && (
              <>
                <span className={styles.count}>{stories?.length ?? 0}</span> {t('New Stories')}
              </>
            )}
          </div>

          {stories?.length > 0 && (
            <div className={styles.storyFilters}>
              {storyFilterOptions.map((option) => (
                <button
                  key={option.key}
                  className={classNames([styles.filterButton, storyFilter === option.key && styles.activeFilter])}
                  onClick={() => setStoryFilter(option.key as 'ALL' | 'MENTIONS' | 'LINKS')}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}

          <LockedWrapper locked={locked} variant="button" tabFunc={tabFunc} onClick={onUnlock}>
            {filteredStories?.length > 0 ? (
              <div className={styles.storiesGrid}>
                {showStoriesList?.map((item, index) =>
                  isStory(item) ? (
                    <div key={item.cover_image + index}>
                      <div
                        className={cn(styles.storyItem, 'group', 'w-full')}
                        onClick={() => handelStoriesListItemClick(item)}
                      >
                        <LocalImage
                          src={item.cover_image}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            overflow: 'hidden',
                          }}
                        />
                        <div className={styles.referContainer} onClick={(e) => e.stopPropagation()}>
                          <StoriesLinkContent links={item.link_urls} referToList={item.mention_names} isDetail={true} />
                        </div>
                        <Button
                          icon={<DownloadIcon className="!flex mx-auto" size={18} />}
                          className={cn('absolute bottom-[18px] right-4 flex group-hover:block hidden !w-7 h-7')}
                          onClick={(e) => {
                            e.stopPropagation();
                            onItemDownloadClick(item);
                          }}
                        />
                      </div>
                      <div>{item.publish_time}</div>
                    </div>
                  ) : (
                    <div key={currentAd.imgUrl_card + index}>
                      <div
                        className="inline-block bg-cover bg-no-repeat aspect-[240/427] rounded-[12px]"
                        style={{ backgroundImage: `url(${currentAd.imgUrl_card})` }}
                      >
                        <div className="h-full flex flex-col px-5 xs:pb-7 xs:pt-10 max-xs:px-3 max-xs:py-3">
                          <h3 className="text-[20px] max-xs:text-base font-bold leading-[1.4] text-white">
                            {currentAd.title}
                          </h3>
                          <p className="text-white text-sm max-xs:text-xs pt-3 flex-1">{currentAd.desc}</p>
                          <Link
                            href={currentAd.link || '/'}
                            className="hover:text-white h-10 max-xs:h-8 bg-[#0c0c0c] rounded-[6px] text-white border-none flex items-center justify-center max-w-[240px]"
                          >
                            {t('Try Now')} <MoveRight className="ml-2" size={16} />
                          </Link>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            ) : stories?.length > 0 ? (
              <div className={styles.emptyContainer}>
                <p>{t('No results found. Try adjusting your filters.')}</p>
              </div>
            ) : (
              <div className={styles.emptyContainer}>
                <p>{t('Keep an eye out for upcoming Stories and stay tuned for updates!')}</p>
              </div>
            )}

            {showStoriesMore && (
              <div className={styles.moreButton} onClick={() => setIndex(() => index + 1)}>
                {t('More')}
              </div>
            )}

            {showStoriesMore && !showDownload && <div className={styles.line} />}
          </LockedWrapper>
        </div>

        <div
          className={styles.highlightsSection}
          style={{ display: tabIndexSelect === 'highlights' ? 'block' : 'none' }}
        >
          <div className={styles.sectionTitle}>
            <span className={styles.count}>{highlights_list?.length ?? 0}</span> {t('New Highlights')}
          </div>

          <LockedWrapper locked={locked} variant="button" tabFunc={tabFunc} onClick={onUnlock}>
            {highlights_list?.length > 0 && (
              <div className={styles.highlightsGrid}>
                {showHighlightsList?.map((item, index) => {
                  return (
                    <div
                      className={styles.highlightItem}
                      key={item?.cover_media_url + index + item?.created_at}
                      onClick={async () => {
                        // 避免重复点击
                        if (!locked && !highlightLoading) {
                          // 立即显示蒙层和 loading 状态
                          setShowHighlightModal(true);
                          setHighlightLoading(true);
                          setCurrentHighlightData(null); // 清空之前的数据

                          try {
                            const res = await fetchInstagramAnonymouslyHighlightsDetailDataClient(
                              item?.highlight_id,
                              locale
                            );
                            if (res?.code === 0 && res?.data?.stories) {
                              setCurrentHighlightData(res.data.stories);
                            }
                          } catch (error) {
                            console.error('Error fetching highlight data:', error);
                            // 如果出错，关闭蒙层
                            setShowHighlightModal(false);
                          } finally {
                            setHighlightLoading(false);
                          }
                        }
                      }}
                    >
                      <LocalImage
                        width={100}
                        height={100}
                        src={item?.cover_media_url}
                        className={styles.highlightImage}
                      />
                      <span className={styles.highlightName}>{item?.highlight_title}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </LockedWrapper>

          {/* 锁定状态不显示加载更多按钮 */}
          {!locked && showHighlightsMore && (
            <div className={styles.moreButton} onClick={() => setHighlightsIndex(() => highlightsIndex + 1)}>
              {t('More')}
            </div>
          )}

          {!locked && showHighlightsMore && !showDownload && <div className={styles.line} />}

          {!(highlights_list?.length > 0) && (
            <div className={styles.emptyContainer}>
              <p>{t('Past Highlights will be available here—Stay Tuned!')}</p>
            </div>
          )}
        </div>
        <div style={{ display: tabIndexSelect === 'post' ? 'block' : 'none' }}>
          <LockedWrapper locked={locked} variant="button" tabFunc={tabFunc} onClick={onUnlock}>
            <PostResultContent
              showDownload={showDownload}
              hideTitle={hideTitle}
              locked={locked}
              loadMoreNotCheckLogin={loadMoreNotCheckLogin}
              post_page_token={post_page_token}
              dataList={locked ? post_list?.slice(0, 3) : post_list}
              gotoPlanClick={gotoPlanClick}
              analyzeAll={analyzeAll}
            />
          </LockedWrapper>
        </div>
      </div>

      {/* 全屏蒙层显示 VideoPlayContent */}
      {showVideoModal && currentStoryData && (
        <div className="fixed inset-0 z-[1001]">
          <VideoPlayContent
            data={{
              storyList: [
                {
                  story_id: currentStoryData.story_id,
                  cover_image: currentStoryData.cover_image,
                  publish_time: currentStoryData.publish_time,
                  media_type: currentStoryData.media_type,
                  video_url: currentStoryData.video_url,
                  mention_names: currentStoryData.mention_names,
                  link_urls: currentStoryData.link_urls,
                },
              ],
              locale: locale,
              media_name: media_name,
            }}
            onClose={() => {
              setShowVideoModal(false);
              setCurrentStoryData(null);
            }}
          />
        </div>
      )}

      {/* 全屏蒙层显示 Highlights */}
      {showHighlightModal && (
        <div className="fixed inset-0 z-[1001]">
          {currentHighlightData ? (
            <VideoPlayContent
              data={{
                storyList: currentHighlightData,
                locale: locale,
                media_name: media_name,
              }}
              onClose={() => {
                setShowHighlightModal(false);
                setCurrentHighlightData(null);
              }}
            />
          ) : (
            <div className="absolute inset-0 bg-black/90">
              <div
                className="absolute top-4 right-4 text-white text-2xl cursor-pointer z-10 hover:opacity-70 transition-opacity"
                onClick={() => {
                  setShowHighlightModal(false);
                  setCurrentHighlightData(null);
                }}
              >
                ✕
              </div>
              <div className="fixed inset-0 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                  <div className="w-16 h-16 border-4 border-white/30 border-t-white rounded-full animate-spin" />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <LimitAlert
        open={showLimit}
        closeAction={(isOk) => {
          setShowLimit(false);
          if (isOk) {
            setTimeout(() => {
              gotoPlanClick && gotoPlanClick();
              const dom = document.getElementById('1');
              dom && dom.scrollIntoView({ behavior: 'smooth' });
            }, 20);
          }
        }}
      />
    </div>
  );
};

export default InstagramStoryContent;
